// Estado de cada medida na tela do quarto.
//
// A regra existe por seguranca, nao por estetica: quem entra no quarto — o
// medico da visita, a enfermeira — precisa saber em DOIS SEGUNDOS o que esta
// sendo medido agora e o que e foto de um momento passado. Sem isso, um numero
// de cinco horas atras passa por leitura atual.

import type { LeituraDTO } from '@medicov1/shared';

export type Estado = 'ao_vivo' | 'recente' | 'antiga' | 'ausente';

/** Sem dado novo por mais que isto, a medida contínua deixa de ser "ao vivo". */
export const JANELA_AO_VIVO_MS = 90_000;
/** Acima disto a medida aparece em âmbar: envelhecer tem que ser visível. */
export const JANELA_ANTIGA_MS = 2 * 60 * 60 * 1000;

/**
 * So e "ao vivo" o que chega sozinho e chegou agora: sensor continuo ou
 * pulseira transmitindo. Medida de aparelho que so fala quando alguem aperta o
 * botao — pressao, glicosimetro — e pontual por natureza, mesmo recem-chegada.
 */
const ORIGEM_CONTINUA = new Set(['sensor_continuo']);

export function estadoDaLeitura(leitura: LeituraDTO | undefined, agora = Date.now()): Estado {
  if (!leitura) return 'ausente';
  const idade = agora - new Date(leitura.recebidoEm).getTime();

  const contínua =
    ORIGEM_CONTINUA.has(leitura.origem) ||
    // FC por pulseira: so conta como continua enquanto os dados seguem chegando.
    (leitura.tipo === 'frequencia_cardiaca' && leitura.origem === 'ble' && leitura.aparelho?.tipo === 'cardio');

  if (contínua && idade <= JANELA_AO_VIVO_MS) return 'ao_vivo';
  if (idade <= JANELA_ANTIGA_MS) return 'recente';
  return 'antiga';
}

export function idadeEmTexto(iso: string, agora = Date.now()): string {
  const minutos = Math.floor((agora - new Date(iso).getTime()) / 60_000);
  if (minutos < 1) return 'agora';
  if (minutos < 60) return `há ${minutos} min`;
  const horas = Math.floor(minutos / 60);
  const resto = minutos % 60;
  if (horas < 24) return resto ? `há ${horas} h ${resto} min` : `há ${horas} h`;
  const dias = Math.floor(horas / 24);
  return `há ${dias} d ${horas % 24} h`;
}

export const ROTULO_ORIGEM: Record<string, string> = {
  ble: 'Bluetooth',
  manual: 'digitado à mão',
  sensor_continuo: 'sensor contínuo',
  exame: 'exame de laboratório',
};

/** Frase que explica o estado. Aparece inteira na faixa, sem sigla. */
export function explicacao(leitura: LeituraDTO | undefined, estado: Estado): string {
  if (!leitura || estado === 'ausente') return 'nenhuma leitura registrada';

  const origem = ROTULO_ORIGEM[leitura.origem] ?? leitura.origem;
  const aparelho = leitura.aparelho ? ` · ${leitura.aparelho.apelido}` : '';

  if (estado === 'ao_vivo') return `medindo agora${aparelho}`;
  return `medida ${idadeEmTexto(leitura.medidoEm)} · ${origem}${aparelho}`;
}
